import BannerColumn from "@/components/BannerBlock"
import ContentColumn from "@/components/page/ContentColumn"
import CtaColumn from "@/components/page/CtaColumn"
import ImageColumn from "@/components/page/ImageColumn"
import { BannerColumnData, Block, BlockColumn, CollectionsColumnData, ContentColumnData, CtaColumnData, DocumentColumnData, FaqColumnData, FormColumnData, HtmlColumnData, ImageColumnData, NewsColumnData, EventColumnData, StatsColumnData, VideoColumnData, ProductVariantsColumnData, KitsColumnData, BenefitsColumnData, CopyrightColumnData } from "@/types/pages/blocks"
import { lazy, Suspense, useCallback } from "react"
import { useMediaQuery } from "@/hooks/useMediaQuery"

const CollectionsColumn = lazy(() => import("@/components/page/CollectionsColumn"))
const DocumentColumn = lazy(() => import("@/components/page/DocumentColumn"))
const FaqColumn = lazy(() => import("@/components/page/FaqColumn"))
const FormColumn = lazy(() => import("@/components/page/FormColumn"))
const HtmlColumn = lazy(() => import("@/components/page/HtmlColumn"))
const NewsColumn = lazy(() => import("@/components/page/NewsColumn"))
const EventColumn = lazy(() => import("@/components/page/EventColumn"))
const StatsColumn = lazy(() => import("@/components/page/StatsColumn"))
const VideoColumn = lazy(() => import("@/components/page/VideoColumn"))
const ProductVariantsColumn = lazy(() => import("@/components/page/ProductVariantsColumn"))
const KitsColumn = lazy(() => import("@/components/page/KitsColumn"))
const BenefitsColumn = lazy(() => import("@/components/page/BenefitsColumn"))
const CopyrightColumn = lazy(() => import("@/components/page/CopyrightColumn"))

export default function PageBlock(props: Block) {
  const { columns } = props

  const isMobile = useMediaQuery('(max-width: 768px)')

  const renderColumnContent = useCallback((column: BlockColumn) => {
    switch (column.type) {
      case 'banner': {
        const bannerData = column.data as BannerColumnData
        const banner = bannerData?.banner
        return banner ? <BannerColumn banner={banner} /> : null
      }
      case 'image': {
        const imageData = column.data as ImageColumnData
        return <ImageColumn {...imageData} />
      }
      case 'video': {
        const videoData = column.data as VideoColumnData
        return <Suspense fallback={null}><VideoColumn {...videoData} /></Suspense>
      }
      case 'cta': {
        const ctaData = column.data as CtaColumnData
        return <CtaColumn {...ctaData} />
      }
      case 'content': {
        const contentData = column.data as ContentColumnData
        return <ContentColumn {...contentData} />
      }
      case 'stats': {
        const statsData = column.data as StatsColumnData
        return <Suspense fallback={null}><StatsColumn {...statsData} /></Suspense>
      }
      case 'faq': {
        const faqData = column.data as FaqColumnData
        return faqData.faq ? <Suspense fallback={null}><FaqColumn faq={faqData.faq} /></Suspense> : null
      }
      case 'document': {
        const documentData = column.data as DocumentColumnData
        return documentData.document ? <Suspense fallback={null}><DocumentColumn document={documentData.document} /></Suspense> : null
      }
      case 'news': {
        const newsData = column.data as NewsColumnData
        return newsData.items ? <Suspense fallback={null}><NewsColumn items={newsData.items} /></Suspense> : null
      }
      case 'event': {
        const eventData = column.data as EventColumnData
        return eventData.event ? <Suspense fallback={null}><EventColumn event={eventData.event} /></Suspense> : null
      }
      case 'collections': {
        const collectionsData = column.data as CollectionsColumnData
        return <Suspense fallback={null}><CollectionsColumn filters={collectionsData.filters} /></Suspense>
      }
      case 'kits': {
        const kitsData = column.data as KitsColumnData
        return <Suspense fallback={null}><KitsColumn filters={kitsData.filters} label={column.label} /></Suspense>
      }
      case 'productVariants': {
        const productVariantsData = column.data as ProductVariantsColumnData
        return <Suspense fallback={null}><ProductVariantsColumn filters={productVariantsData.filters} label={column.label} /></Suspense>
      }
      case 'form': {
        const formData = column.data as FormColumnData
        return <Suspense fallback={null}><FormColumn data={formData} /></Suspense>
      }
      case 'html': {
        const htmlData = column.data as HtmlColumnData
        return <Suspense fallback={null}><HtmlColumn data={htmlData} /></Suspense>
      }
      case 'benefits': {
        const benefitsData = column.data as BenefitsColumnData
        return benefitsData.items ? <Suspense fallback={null}><BenefitsColumn items={benefitsData.items} /></Suspense> : null
      }
      case 'copyright': {
        const copyrightData = column.data as CopyrightColumnData
        return <Suspense fallback={null}><CopyrightColumn leftText={copyrightData.leftText} rightText={copyrightData.rightText} /></Suspense>
      }
      default:
        return null
    }
  }, [])

  return (
    <div className="flex flex-wrap items-start w-full gap-4 md:gap-0">
      {columns.map((column, idx) => {
        const width = isMobile ? 100 : (column.css as { width?: number })?.width || 100
        const columnStyle = { ...((column.css as React.CSSProperties) || {}), width: `${width}%` }

        return (
          <div
            key={idx}
            className="rounded-lg flex md:px-4"
            style={columnStyle}
          >
            {renderColumnContent(column)}
          </div>
        )
      })}
    </div>
  )
}
